import * as XLSX from "xlsx";

/**
 * The column the backend fills in. Used only to highlight it in the preview —
 * nothing is validated against it, and its absence changes nothing.
 */
export const FILLED_COLUMN = "CLIENT_IP";

export interface ParsedReport {
  headers: string[];
  rows: Record<string, unknown>[];
}

export const isExcelFile = (file: File): boolean =>
  file.type ===
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
  file.type === "application/vnd.ms-excel" ||
  /\.(xlsx|xls|csv)$/i.test(file.name);

/**
 * Reads the first sheet into objects keyed by header, for display only.
 *
 * The contents are never checked: the backend owns what a valid report is, so
 * the UI does not second-guess the columns or reject a file for its data.
 * Callers must treat a failure here as "no preview", never as "cannot upload".
 */
export const parseReport = (buffer: ArrayBuffer): ParsedReport => {
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return { headers: [], rows: [] };

  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
  });
  if (matrix.length === 0) return { headers: [], rows: [] };

  const headers = (matrix[0] as unknown[]).map((cell) =>
    String(cell ?? "").trim()
  );

  const rows = matrix.slice(1).map((row) => {
    const record: Record<string, unknown> = {};
    headers.forEach((header, index) => {
      record[header] = (row as unknown[])[index] ?? "";
    });
    return record;
  });

  return { headers, rows };
};
